/**
 * TAB 7 — Audit Logs (requirement §51 to §54, AC-28).
 *
 * Administrative and system actions around the treasury. The VC movement itself
 * lives in the ledger (§52); this is who decided what, when, and why.
 *
 * Records are immutable and non-deletable — enforced by beforeSave/beforeDelete
 * guards inside Parse Server, so there is no edit affordance here by design.
 */

import * as React from "react";
import {
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

import {
  TreasuryFilters,
  DataTable,
  Panel,
  SectionTitle,
  ErrorBar,
  Loading,
  ExportButton,
  exportRows,
  fmtDateTime,
  humanise,
  filterInputSx,
  dialogSx,
  secondaryBtnSx,
  GOLD,
  BORDER,
} from "../components/common";
import { getAuditLogs, getAuditDetail } from "../api";

export const AuditTab = ({ filters, onFiltersChange }) => {
  const [state, setState] = React.useState({ data: [], pagination: null, actions: [] });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(25);
  const [action, setAction] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getAuditLogs({ ...filters, action, search, page, limit });
        if (!cancelled) setState(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters, action, search, page, limit]);

  const openDetail = async (auditId) => {
    try {
      const result = await getAuditDetail({ auditId });
      setDetail(result);
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: "auditId", label: "Audit ID", width: 170 },
    { key: "timestamp", label: "Timestamp", render: (r) => fmtDateTime(r.timestamp) },
    { key: "user", label: "User" },
    { key: "role", label: "Role" },
    {
      key: "action",
      label: "Action",
      render: (r) => (
        <Chip
          size="small"
          label={humanise(r.action)}
          sx={{ backgroundColor: "#242426", color: GOLD, fontSize: "0.62rem" }}
        />
      ),
    },
    { key: "module", label: "Module" },
    { key: "entity", label: "Entity" },
    { key: "entityId", label: "Entity ID", width: 150 },
    {
      key: "reason",
      label: "Reason",
      width: 260,
      render: (r) => (
        <Typography sx={{ fontSize: "0.68rem", color: "#BDBDBD" }}>{r.reason || "-"}</Typography>
      ),
    },
    {
      key: "action_btn",
      label: "Action",
      align: "center",
      render: (r) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openDetail(r.auditId);
          }}
          sx={{ color: GOLD, textTransform: "none", fontSize: "0.68rem" }}
        >
          View
        </Button>
      ),
    },
  ];

  const a = detail?.audit || {};

  return (
    <Box>
      <TreasuryFilters
        filters={filters}
        onChange={onFiltersChange}
        extra={
          <>
            <FormControl size="small" sx={{ ...filterInputSx, minWidth: 220 }}>
              <InputLabel>Action</InputLabel>
              <Select label="Action" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
                <MenuItem value="all">All Actions</MenuItem>
                {(state.actions || []).map((act) => (
                  <MenuItem key={act} value={act}>
                    {humanise(act)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search user / reason / entity"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ ...filterInputSx, minWidth: 240 }}
            />
            <ExportButton
              disabled={!state.data?.length}
              onExport={(format) =>
                exportRows(
                  state.data.map((r) => ({
                    "Audit ID": r.auditId,
                    Timestamp: r.timestamp,
                    User: r.user,
                    Role: r.role,
                    Action: r.action,
                    Module: r.module,
                    Entity: r.entity,
                    "Entity ID": r.entityId,
                    Reason: r.reason,
                    Reference: r.reference,
                  })),
                  "house-treasury-audit-logs",
                  format
                )
              }
            />
          </>
        }
      />

      <ErrorBar error={error} onClose={() => setError("")} />

      <SectionTitle
        action={
          <Chip
            size="small"
            icon={<LockIcon sx={{ fontSize: 13 }} />}
            label="Immutable — cannot be edited or deleted"
            sx={{ backgroundColor: "#242426", color: "#9E9E9E", fontSize: "0.62rem" }}
          />
        }
      >
        Audit Logs
      </SectionTitle>

      <DataTable
        dense
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onRowClick={(r) => openDetail(r.auditId)}
        emptyMessage="No audit records for the selected period."
      />

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth sx={dialogSx}>
        <DialogTitle>Audit Detail — {a.auditId}</DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <Loading />
          ) : (
            <Grid container spacing={2}>
              <DetailField label="Audit ID" value={a.auditId} />
              <DetailField label="Timestamp" value={fmtDateTime(a.timestamp)} />
              <DetailField label="User ID" value={a.userId} />
              <DetailField label="User Name" value={a.user} />
              <DetailField label="Role" value={a.role} />
              <DetailField label="Action" value={humanise(a.action)} />
              <DetailField label="Module" value={a.module} />
              <DetailField label="Entity" value={a.entity} />
              <DetailField label="Entity ID" value={a.entityId} />
              <DetailField label="IP Address" value={a.ipAddress} />
              <DetailField label="Device Information" value={a.deviceInfo} full />
              <DetailField label="Reason" value={a.reason} full />
              <DetailField label="Related Transaction" value={a.relatedTransactionId} full />

              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem", mb: 0.5 }}>
                  Before Value
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#1C1C1E",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1,
                    p: 1.5,
                    fontSize: "0.68rem",
                    color: "#BDBDBD",
                    overflowX: "auto",
                    maxHeight: 260,
                  }}
                >
                  {a.beforeValue ? JSON.stringify(a.beforeValue, null, 2) : "—"}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem", mb: 0.5 }}>
                  After Value
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#1C1C1E",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1,
                    p: 1.5,
                    fontSize: "0.68rem",
                    color: "#BDBDBD",
                    overflowX: "auto",
                    maxHeight: 260,
                  }}
                >
                  {a.afterValue ? JSON.stringify(a.afterValue, null, 2) : "—"}
                </Box>
              </Grid>

              {detail.relatedTransaction && (
                <Grid item xs={12}>
                  <Panel>
                    <Typography sx={{ color: GOLD, fontSize: "0.72rem", fontWeight: 700, mb: 1 }}>
                      Related Transaction
                    </Typography>
                    <Typography sx={{ color: "#BDBDBD", fontSize: "0.72rem" }}>
                      {humanise(detail.relatedTransaction.transactionType)} ·{" "}
                      {detail.relatedTransaction.txnId} ·{" "}
                      {detail.relatedTransaction.from?.name} → {detail.relatedTransaction.to?.name}
                    </Typography>
                  </Panel>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetail(null)} sx={secondaryBtnSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const DetailField = ({ label, value, full }) =>
  value === undefined || value === null || value === "" ? null : (
    <Grid item xs={12} md={full ? 12 : 4}>
      <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}>{value}</Typography>
    </Grid>
  );

export default AuditTab;
